/**
 * TGPresetManager
 * Save, load, and manage named table presets (filters, columns, sort)
 */

import React, { useState, useEffect } from 'react';
import { Button, Dropdown, Modal, Input, List, Space, message, Popconfirm } from 'antd';
import { SaveOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { spacing } from '../../../theme/designTokens';

/**
 * @param {Object} props
 * @param {string} props.presetKey - Base key for localStorage
 * @param {Object} props.currentState - Current table state to save
 * @param {Function} props.onLoadPreset - (state) => void - Load preset callback
 */
function TGPresetManager({ presetKey, currentState, onLoadPreset }) {
  const [presets, setPresets] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [manageModalOpen, setManageModalOpen] = useState(false);

  const presetsStorageKey = `${presetKey}_saved_presets`;

  // Load presets from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(presetsStorageKey);
      if (stored) {
        setPresets(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  }, [presetsStorageKey]);

  // Save presets to localStorage
  const savePresets = (updatedPresets) => {
    try {
      localStorage.setItem(presetsStorageKey, JSON.stringify(updatedPresets));
      setPresets(updatedPresets);
    } catch (error) {
      console.error('Failed to save presets:', error);
      message.error('Failed to save presets');
    }
  };

  // Save current state as new preset
  const handleSavePreset = () => {
    if (!newPresetName.trim()) {
      message.error('Please enter a preset name');
      return;
    }

    const existingIndex = presets.findIndex((p) => p.name === newPresetName);

    if (existingIndex >= 0) {
      // Overwrite existing
      const updated = [...presets];
      updated[existingIndex] = {
        name: newPresetName,
        state: currentState,
        savedAt: new Date().toISOString(),
      };
      savePresets(updated);
      message.success(`Preset "${newPresetName}" updated`);
    } else {
      // Create new
      const newPreset = {
        name: newPresetName,
        state: currentState,
        savedAt: new Date().toISOString(),
      };
      savePresets([...presets, newPreset]);
      message.success(`Preset "${newPresetName}" saved`);
    }

    setSaveModalOpen(false);
    setNewPresetName('');
  };

  // Load a preset
  const handleLoadPreset = (preset) => {
    onLoadPreset(preset.state);
    message.success(`Loaded preset "${preset.name}"`);
  };

  // Delete a preset
  const handleDeletePreset = (presetName) => {
    const updated = presets.filter((p) => p.name !== presetName);
    savePresets(updated);
    message.success(`Preset "${presetName}" deleted`);
  };

  // Export presets to JSON file
  const handleExportPresets = () => {
    const dataStr = JSON.stringify(presets, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `table-presets-${presetKey}.json`;
    link.click();
    message.success('Presets exported');
  };

  // Import presets from JSON file
  const handleImportPresets = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (Array.isArray(imported)) {
          savePresets([...presets, ...imported]);
          message.success(`Imported ${imported.length} presets`);
        } else {
          message.error('Invalid preset file');
        }
      } catch (error) {
        console.error('Import failed:', error);
        message.error('Failed to import presets');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset file input
  };

  const menuItems = [
    {
      key: 'save',
      label: 'Save Current View',
      icon: <SaveOutlined />,
      onClick: () => setSaveModalOpen(true),
    },
    {
      key: 'manage',
      label: 'Manage Presets',
      onClick: () => setManageModalOpen(true),
      disabled: presets.length === 0,
    },
    {
      type: 'divider',
    },
    ...(presets.length > 0
      ? presets.map((preset) => ({
          key: `load-${preset.name}`,
          label: preset.name,
          onClick: () => handleLoadPreset(preset),
        }))
      : [
          {
            key: 'empty',
            label: 'No saved presets',
            disabled: true,
          },
        ]),
  ];

  return (
    <>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
        <Button icon={<SaveOutlined />}>Presets</Button>
      </Dropdown>

      {/* Save Preset Modal */}
      <Modal
        title="Save Current View as Preset"
        open={saveModalOpen}
        onCancel={() => {
          setSaveModalOpen(false);
          setNewPresetName('');
        }}
        onOk={handleSavePreset}
        okText="Save"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>Enter a name for this preset:</div>
          <Input
            placeholder="e.g., My Audit View"
            value={newPresetName}
            onChange={(e) => setNewPresetName(e.target.value)}
            onPressEnter={handleSavePreset}
            autoFocus
          />
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
            Saves filters, column visibility, column order, sort, and page size
          </div>
        </Space>
      </Modal>

      {/* Manage Presets Modal */}
      <Modal
        title="Manage Presets"
        open={manageModalOpen}
        onCancel={() => setManageModalOpen(false)}
        footer={[
          <label key="import" htmlFor="preset-import-input">
            <Button icon={<UploadOutlined />}>Import</Button>
            <input
              id="preset-import-input"
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImportPresets}
            />
          </label>,
          <Button key="export" icon={<DownloadOutlined />} onClick={handleExportPresets}>
            Export All
          </Button>,
          <Button key="close" onClick={() => setManageModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <List
          dataSource={presets}
          renderItem={(preset) => (
            <List.Item
              actions={[
                <Button
                  key="load"
                  type="link"
                  size="small"
                  onClick={() => {
                    handleLoadPreset(preset);
                    setManageModalOpen(false);
                  }}
                >
                  Load
                </Button>,
                <Popconfirm
                  key="delete"
                  title="Delete this preset?"
                  onConfirm={() => handleDeletePreset(preset.name)}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={preset.name}
                description={`Saved ${new Date(preset.savedAt).toLocaleDateString()}`}
              />
            </List.Item>
          )}
          locale={{ emptyText: 'No saved presets' }}
        />
      </Modal>
    </>
  );
}

export default TGPresetManager;
